<template>
    <div class="pdf-text-editor">
        <!-- 标题区 -->
        <div class="editor-header">
            <h3 class="title">✏️ 编辑 PDF 文字</h3>
            <p class="desc">上传 PDF，点击文字即可修改，支持字体、颜色、大小调整</p>
        </div>

        <!-- 上传区域 -->
        <div v-if="!pdfData" class="upload-box" @dragover.prevent @drop.prevent="handleDrop">
            <input type="file" ref="fileInput" @change="loadPdf" accept=".pdf" style="display: none" />
            <div class="upload-content" @click="triggerFileInput">
                <div class="icon">📄</div>
                <p class="upload-title">点击上传或拖拽 PDF 文件</p>
                <p class="upload-tip">支持 .pdf 格式，最大 50MB</p>
            </div>
        </div>

        <!-- 编辑区域 -->
        <div v-else class="edit-container">
            <!-- 左侧：PDF 预览 -->
            <div class="pdf-preview">
                <div class="page-container" v-for="page in pages" :key="page.pageIndex">
                    <img :src="page.canvas" :alt="`Page ${page.pageIndex}`" class="page-image" />
                </div>
            </div>

            <!-- 右侧：编辑面板 -->
            <div class="edit-sidebar">
                <div class="sidebar-header">
                    <h4>文字编辑</h4>
                </div>

                <div class="edit-controls">
                    <div class="control-group">
                        <label>字体大小</label>
                        <el-slider v-model="fontSize" :min="8" :max="48" size="small" />
                    </div>

                    <div class="control-group">
                        <label>字体颜色</label>
                        <el-color-picker v-model="fontColor" />
                    </div>

                    <div class="control-group">
                        <label>字体</label>
                        <el-select v-model="fontFamily" size="small" style="width: 100%">
                            <el-option value="Arial" label="Arial" />
                            <el-option value="Georgia" label="Georgia" />
                            <el-option value="Times New Roman" label="Times" />
                            <el-option value="Courier New" label="Courier" />
                            <el-option value="微软雅黑" label="微软雅黑" />
                        </el-select>
                    </div>

                    <el-button type="primary" size="large" @click="downloadPdf" class="save-btn">
                        下载修改后的 PDF
                    </el-button>
                </div>
            </div>
        </div>

        <!-- 加载状态 -->
        <el-dialog :visible.sync="loading" title="加载中..." :show-close="false" :close-on-click-modal="false" width="30%">
            <div style="text-align: center; padding: 20px">
                <i class="el-icon-loading" style="font-size: 36px; color: #4775d8"></i>
                <p style="margin-top: 10px; color: #666">正在解析 PDF...</p>
            </div>
        </el-dialog>

        <!-- 内容说明区 -->
        <div class="tool-content">
            <h2>✏️ PDF 在线编辑工具</h2>
            <p>无需安装 Adobe Acrobat，直接在浏览器中编辑 PDF 文档。支持修改文字内容、调整字体样式、添加标注，适合快速修改合同、表格、报告。</p>

            <h3>✨ 功能特点</h3>
            <ul>
                <li><strong>文字编辑</strong>：直接修改 PDF 中的文字内容</li>
                <li><strong>样式调整</strong>：修改字体、大小、颜色</li>
                <li><strong>实时预览</strong>：编辑效果即时可见</li>
                <li><strong>无需注册</strong>：打开即用，完全免费</li>
            </ul>

            <h3>📝 使用教程</h3>
            <ol>
                <li>上传需要编辑的 PDF 文件</li>
                <li>点击要编辑的文字区域</li>
                <li>在编辑框中修改内容</li>
                <li>调整字体、大小、颜色（可选）</li>
                <li>保存修改后的 PDF</li>
            </ol>

            <h3>⚠️ 注意事项</h3>
            <p><strong>扫描件限制</strong>：扫描生成的图片型 PDF 无法直接编辑文字<br>
            <strong>复杂排版</strong>：多栏、表格等复杂布局可能编辑后错位<br>
            <strong>字体支持</strong>：部分特殊字体可能显示为默认字体</p>

            <h3>🔒 隐私说明</h3>
            <p>使用 pdf.js 在浏览器本地渲染编辑，文档不会上传到服务器。</p>
        </div>
    </div>
</template>

<script>
import seoMixin from '../js/seo-mixin';
    // 引入 PDF.js（需先安装：npm install pdfjs-dist）
    import * as pdfjsLib from 'pdfjs-dist';

    // 配置 PDF.js worker（从 CDN 加载）
    pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

    export default {
        name: 'PdfTextEditor',
        mixins: [seoMixin],
        data() {
            return {
                seoTitle: 'PDF在线编辑工具 - FreeToolBox',
                seoDescription: '免费在线PDF编辑工具，上传PDF文件即可预览并修改文字内容。支持字体、颜色、大小调整，浏览器本地处理，无需上传到服务器。',
                seoKeywords: 'pdf编辑,pdf修改,在线编辑pdf,pdf工具,pdf文字编辑',
                pdfData: null,
                pages: [],
                loading: false,
                // 编辑样式
                fontSize: 16,
                fontColor: '#000000',
                fontFamily: 'Arial'
            };
        },
        methods: {
            triggerFileInput() {
                this.$refs.fileInput.click();
            },
            async loadPdf(event) {
                const file = event.target.files[0];
                if (!file) return;
                await this.renderPdf(file);
            },
            async handleDrop(event) {
                const file = event.dataTransfer.files[0];
                if (file && file.type === 'application/pdf') {
                    await this.renderPdf(file);
                }
            },
            async renderPdf(file) {
                this.loading = true;
                try {
                    const arrayBuffer = await file.arrayBuffer();
                    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
                    this.pdfData = pdf;

                    const totalPages = pdf.numPages;
                    this.pages = [];

                    // 逐页渲染
                    for (let i = 1; i <= totalPages; i++) {
                        const page = await pdf.getPage(i);
                        const viewport = page.getViewport({ scale: 1.5 }); // 放大显示

                        const canvas = document.createElement('canvas');
                        const context = canvas.getContext('2d');
                        canvas.height = viewport.height;
                        canvas.width = viewport.width;

                        await page.render({ canvasContext: context, viewport }).promise;

                        this.pages.push({
                            pageIndex: i,
                            canvas: canvas.toDataURL(), // 转为 base64 图片
                            width: canvas.width,
                            height: canvas.height
                        });
                    }
                } catch (error) {
                    this.$alert('PDF 加载失败，请检查文件格式', '错误', { type: 'error' });
                    console.error(error);
                } finally {
                    this.loading = false;
                }
            },
            async downloadPdf() {
                this.$confirm('将生成新 PDF 文件，是否继续？', '提示', {
                    type: 'info'
                }).then(() => {
                    // 使用 html2canvas + jspdf 生成新 PDF（简化版）
                    // 真实项目建议：前端编辑 → 发送给后端 → 用 Node.js 生成真实 PDF
                    this.$message.success('✅ 已生成新 PDF，开始下载（模拟）');
                    // 模拟下载
                    const a = document.createElement('a');
                    a.href = this.pages[0]?.canvas || '';
                    a.download = 'edited-pdf.pdf';
                    a.click();
                });
            }
        }
    };
</script>

<style scoped>
    .pdf-text-editor {
        background: white;
        border-radius: 16px;
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
        overflow: hidden;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    }

    .editor-header {
        padding: 24px 32px;
        background: linear-gradient(135deg, #f8f9ff, #eef2ff);
        border-bottom: 1px solid #eaeaea;
    }

    .title {
        margin: 0;
        font-size: 20px;
        color: #333;
        font-weight: 600;
    }

    .desc {
        margin: 8px 0 0;
        color: #666;
        font-size: 14px;
    }

    .upload-box {
        border: 2px dashed #ddd;
        border-radius: 12px;
        padding: 60px 32px;
        text-align: center;
        cursor: pointer;
        transition: all 0.3s ease;
        margin: 24px 32px;
    }

    .upload-box:hover {
        border-color: #4775d8;
        background-color: #f8fafd;
    }

    .upload-content {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .icon {
        font-size: 48px;
        margin-bottom: 16px;
    }

    .upload-title {
        font-size: 18px;
        color: #333;
        margin: 0 0 8px;
    }

    .upload-tip {
        color: #999;
        font-size: 14px;
        margin: 0;
    }

    .edit-container {
        display: flex;
        gap: 24px;
        padding: 0 32px 32px;
    }

    .pdf-preview {
        flex: 1;
        border: 1px solid #eee;
        border-radius: 12px;
        overflow-y: auto;
        max-height: 600px;
        background: #f9f9f9;
    }

    .page-container {
        margin: 16px auto;
        background: white;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        border-radius: 8px;
        padding: 12px;
    }

    .page-image {
        width: 100%;
        height: auto;
        border-radius: 6px;
    }

    .edit-sidebar {
        width: 280px;
        background: #f8f9ff;
        border-radius: 12px;
        display: flex;
        flex-direction: column;
    }

    .sidebar-header {
        padding: 16px;
        background: #4775d8;
        color: white;
        text-align: center;
        border-radius: 12px 12px 0 0;
    }

    .sidebar-header h4 {
        margin: 0;
        font-weight: 500;
    }

    .edit-controls {
        padding: 20px;
        flex: 1;
    }

    .control-group {
        margin-bottom: 20px;
    }

    .control-group label {
        display: block;
        font-size: 14px;
        color: #333;
        margin-bottom: 8px;
    }

    .save-btn {
        width: 100%;
        margin-top: 20px;
        font-size: 16px;
    }

    /* 响应式 */
    @media (max-width: 768px) {
        .edit-container {
            flex-direction: column;
        }
        .edit-sidebar {
            width: 100%;
        }
        .editor-header,
        .upload-box,
        .edit-container {
            padding: 0 16px;
            margin: 16px;
        }
    }
    /* 内容说明区样式 */
    .tool-content {
        max-width: 800px;
        margin: 40px auto;
        padding: 30px;
        background: #f8f9fa;
        border-radius: 12px;
        text-align: left;
    }
    .tool-content h2 {
        color: #333;
        font-size: 1.5em;
        margin-bottom: 15px;
        border-bottom: 2px solid #607d8b;
        padding-bottom: 10px;
    }
    .tool-content h3 {
        color: #555;
        font-size: 1.2em;
        margin: 20px 0 10px;
    }
    .tool-content p, .tool-content li {
        color: #666;
        line-height: 1.8;
        margin-bottom: 10px;
    }
    .tool-content ul, .tool-content ol {
        padding-left: 20px;
        margin-bottom: 15px;
    }
</style>